// src/pages/Moderation/UsersViolationsPage/model/useUsersViolations.ts
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getModerationFlagsRequest,
  getModerationUsersRequest,
} from 'redux/reducers/moderation/reducer';
import {
  getModerationFlagsSelector,
  getModerationUsersSelector,
} from 'redux/reducers/moderation/selectors';

import { ModerationUser, UsersCounters, UsersViolationsFilter } from '../types';
import { buildRestrictionAgg } from '../utils';

type OrderByField = 'created_at' | 'username' | 'email';
export type SortState = { asc: boolean };

type UsersResponseData =
  | ModerationUser[]
  | {
      users: ModerationUser[];
      total?: number | string;
      count?: number | string;
      users_counters?: UsersCounters;
    };

type UsersResp = {
  status?: string;
  data?: UsersResponseData;
};

function useDebounce<T>(v: T, delay = 400): T {
  const [d, setD] = useState(v);
  useEffect(() => {
    const id = setTimeout(() => setD(v), delay);
    return () => clearTimeout(id);
  }, [v, delay]);
  return d;
}

function safeNum(v: unknown, fallback = 0): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function useUsersViolations() {
  const dispatch = useDispatch();
  const usersResp = useSelector(getModerationUsersSelector) as UsersResp | null;
  const flags = useSelector(getModerationFlagsSelector);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const [filters, setFilters] = useState<UsersViolationsFilter>({
    search: '',
    onlyWithRestrictions: true,
    restriction: undefined,
    dateFrom: undefined,
    dateTo: undefined,
    status: undefined,
    flag: undefined,
  });

  const [sort, setSort] = useState<SortState>({ asc: false });
  const debouncedFilters = useDebounce(filters, 400);

  // ✅ list адаптер
  const rawList: ModerationUser[] = useMemo(() => {
    const d = usersResp?.data;
    if (!d) return [];
    if (Array.isArray(d)) return d;
    if (Array.isArray(d.users)) return d.users;
    return [];
  }, [usersResp]);

  // ✅ total: підтримка total | count | fallback на довжину (щоб не було NaN)
  const total: number = useMemo(() => {
    const d = usersResp?.data;
    if (!d) return 0;

    if (Array.isArray(d)) {
      // якщо бек колись віддасть просто масив без total
      return d.length;
    }

    return safeNum(d.total ?? d.count ?? rawList.length, rawList.length);
  }, [usersResp, rawList]);

  // ✅ counters: тільки якщо object-формат
  const counters: UsersCounters | null = useMemo(() => {
    const d = usersResp?.data;
    if (!d || Array.isArray(d)) return null;
    return (d.users_counters ?? null) as UsersCounters | null;
  }, [usersResp]);

  const topRestrictions = useMemo(() => buildRestrictionAgg(rawList).slice(0, 8), [rawList]);

  const restrictionsOptions = useMemo(() => {
    return topRestrictions.map((x) => ({
      id: String(x.restriction),
      label: `Restriction ${x.restriction} · ${x.cnt}`,
    }));
  }, [topRestrictions]);

  const payload = useMemo(() => {
    const p: any = {
      current_page: page,
      per_page: perPage,
      asc: sort.asc,
    };

    if (debouncedFilters.search) p.search = debouncedFilters.search;
    if (debouncedFilters.dateFrom) p.date_from = debouncedFilters.dateFrom;
    if (debouncedFilters.dateTo) p.date_to = debouncedFilters.dateTo;
    if (debouncedFilters.status) p.status = debouncedFilters.status;
    if (typeof debouncedFilters.restriction === 'number')
      p.restriction = debouncedFilters.restriction;
    if (typeof debouncedFilters.flag === 'number') p.flag = debouncedFilters.flag;

    return p;
  }, [page, perPage, sort, debouncedFilters]);

  useEffect(() => {
    dispatch(getModerationUsersRequest(payload));
    dispatch(getModerationFlagsRequest());
  }, [dispatch, payload]);

  useEffect(() => {
    setPage(1);
  }, [debouncedFilters, sort, perPage]);

  return {
    users: rawList,
    total,
    counters,
    topRestrictions,
    restrictionsOptions,
    flags,

    page,
    perPage,
    filters,
    sort,

    setPage,
    setPerPage,
    setFilters,
    setSort,
  };
}
